﻿import { getLegendDisplayPalettes, Palette } from '@fe/weather';
import React, { useMemo } from 'react';
import useWeatherService from '../../../../../components/hooks/use-weather-service';
import useAllWorkspacesSelectedWeather from '../../../../../components/weather/hooks/use-all-workspaces-selected-weather';
import useWeatherSettings from '../../../../../components/weather/settings/sections/hooks/useSettings';
import { UDViewTerrainShadingType } from '../../../../../udView/udview';
import { OpenInMultiWindowButton } from '../../../../components/multi-window/OpenMultiWindowButton';
import { WorkspaceWindowPanelType } from '../../../../components/multi-window/types';
import { useMapIsLight } from '../../../../hooks/use-map';
import { useWorkspaceView } from '../../hooks/use-view';
import { PanelTitleTrailingContent } from '../HomePanel';
import CategoricalIndicator from './CategoricalIndicator';
import Combined from './Combined';
import DiscreteBar from './DiscreteBar';
import GradientBar from './GradientBar';
import { MapLegendMap } from './MapLegendMap';
import {
    getUdViewStaticDataKey,
    navLayerKeyMap,
    navLayersPalettes,
    terrainShadingPalette,
    UDViewStaticDataKeysWithShow
} from './palettes/nav-layers';
import { planeHeaderPalette, planeMiscPalette, planeRoutePalette } from './palettes/planes';
import { UdViewStaticDataKey } from './palettes/types';
import { BarOrientation } from './types';

export function MapLegend() {
    const selectedWeather = useAllWorkspacesSelectedWeather();
    const weatherSettings = useWeatherSettings();
    const mapIsLight = useMapIsLight();
    const navData = useWorkspaceView((state) => state.staticData);
    const planeUdView = useWorkspaceView((state) => state.plane);
    const weatherService = useWeatherService();
    const showWeather = useWorkspaceView((state) => state.weather.show);
    const terrainShading = useWorkspaceView((x) => x.map.terrainShading);

    const weatherPalettes: Palette[] = React.useMemo(() => {
        if (!showWeather || !weatherSettings) {
            // TODO use use ud view
            return [];
        }
        const list: number[] = [];

        for (const item of selectedWeather.filter((x) => x.loaded && x.success && x.visible && (x.weatherStore?.frames?.length ?? 0) > 0)) {
            const paletteId = item.weatherStore!.frames[0].header.productPallet;
            if (!list.includes(paletteId)) {
                list.push(paletteId);
            }
        }

        return list
            .map((x) => getLegendDisplayPalettes(x, weatherSettings, mapIsLight))
            .flat()
            .filter((x) => x && !x.hideFromLegend) as Palette[];
    }, [selectedWeather, weatherSettings, mapIsLight, weatherService]);

    const layerPalettes: Palette[] = React.useMemo(() => {
        return Object.keys(navLayerKeyMap)
            .filter((key) => navData[key as UDViewStaticDataKeysWithShow].show)
            .map((x) => getUdViewStaticDataKey(x as UDViewStaticDataKeysWithShow))
            .filter((x) => x !== undefined)
            .map((x) => navLayersPalettes.find((pal) => pal.id === x))
            .filter(Boolean) as Palette[];
    }, [navData]);

    const renderIndicator = (palette: Palette | null) => {
        if (!palette) {
            return null;
        }

        switch (palette.type) {
            case 'discreteBar':
                return <DiscreteBar key={palette.id} data={palette} orientation={BarOrientation.Vertical} />;
            case 'gradientBar':
                return <GradientBar key={palette.id} data={palette} orientation={BarOrientation.Vertical} />;
            case 'categorical':
                return <CategoricalIndicator key={palette.id} data={palette} />;
            case 'combined':
                return <Combined key={palette.id} palette={palette} />;
            default:
                return null;
        }
    };

    const terrainPalette = useMemo(() => {
        if (!terrainShading.enabled) {
            return null;
        }

        if (terrainShading.type === UDViewTerrainShadingType.colorRamp) {
            return terrainShadingPalette;
        }

        return {
            id: UdViewStaticDataKey.TerrainShading,
            name: 'Terrain shading',
            type: 'categorical',
            entries: [{ label: `>= ${terrainShading.altitude}ft`, color: terrainShading.color }],
            withValues: false
        } satisfies Palette;
    }, [terrainShading]);

    return (
        <div className="d-flex flex-column h-100">
            <PanelTitleTrailingContent>
                <OpenInMultiWindowButton type={WorkspaceWindowPanelType.mapLegend} />
            </PanelTitleTrailingContent>
            <div
                className="p-1 flex-grow-1 min-0 overflow-auto d-flex flex-column gap-1"
                style={{ fontSize: '0.8rem', backgroundColor: 'var(--kendo-color-surface-alt)' }}>
                <div>Map</div>
                <MapLegendMap />
                {terrainPalette && renderIndicator(terrainPalette)}

                {planeUdView?.settings.show && (
                    <div>
                        <div className="pt-1 border-top">Aircraft</div>
                        {renderIndicator(planeHeaderPalette(planeUdView))}
                        {renderIndicator(planeRoutePalette(planeUdView))}
                        {renderIndicator(planeMiscPalette(planeUdView))}
                    </div>
                )}

                {layerPalettes.length > 0 && (
                    <div>
                        <div className="pt-1 border-top">Layers</div>
                        {layerPalettes.map((palette) => renderIndicator(palette))}
                    </div>
                )}

                {weatherPalettes.length > 0 && (
                    <div>
                        <div className="pt-1 border-top">Weather</div>
                        {weatherPalettes.map((legend) => {
                            return renderIndicator(legend);
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
